import { useField } from "formik";
import type { ReactNode } from "react";
import { FormField } from "@/components/common/form-field";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

/** Shared micro-label style used across the limited-field 433 forms. */
export const FIELD_LABEL = "text-muted-foreground text-xs font-semibold uppercase tracking-wide";

/**
 * A Formik-bound money input. Stores the raw dollar string (e.g. "1234.56") in
 * Formik; the save layer converts to integer cents via `dollarsToCents`.
 * Restricts input to a non-negative decimal with at most two places.
 */
export function MoneyInput({
  name,
  label,
  className,
}: {
  name: string;
  label: string;
  className?: string;
}) {
  const [, , helpers] = useField<string>(name);
  return (
    <FormField
      name={name}
      label={label}
      labelClassName={FIELD_LABEL}
      className={className}
      inputMode="decimal"
      placeholder="0.00"
      icon={<span className="text-muted-foreground text-sm">$</span>}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === "" || /^\d*\.?\d{0,2}$/.test(raw)) helpers.setValue(raw);
      }}
    />
  );
}

/** Formik-bound shadcn Select. Options are plain value/label pairs. */
export function SelectInput({
  name,
  label,
  options,
  placeholder = "Select…",
  className,
}: {
  name: string;
  label: string;
  options: { value: string; label: string }[];
  placeholder?: string;
  className?: string;
}) {
  const [field, meta, helpers] = useField<string>(name);
  const hasError = meta.touched && !!meta.error;
  return (
    <div className={cn("grid gap-2", className)}>
      <Label className={FIELD_LABEL}>{label}</Label>
      <Select value={field.value || ""} onValueChange={(v) => helpers.setValue(v)}>
        <SelectTrigger className="w-full" aria-invalid={hasError}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hasError && <p className="text-destructive text-sm">{meta.error}</p>}
    </div>
  );
}

/** Formik-bound checkbox with an inline label. */
export function CheckboxInput({
  name,
  label,
  className,
}: {
  name: string;
  label: ReactNode;
  className?: string;
}) {
  const [field, , helpers] = useField<boolean>({ name, type: "checkbox" });
  return (
    <label className={cn("flex items-center gap-2 text-sm", className)}>
      <Checkbox checked={!!field.value} onCheckedChange={(c) => helpers.setValue(c === true)} />
      <span>{label}</span>
    </label>
  );
}
